import React from 'react';
import { ArrowLeftIcon, WalletIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../hooks/useApp';
import { useAccount, useDisconnect } from 'wagmi';
import useWallet from '../hooks/useWallet';
import useGameStore from '../stores/gameStore';

const LeftSidebar = ({ isOpen, onClose, isGamePage }) => {
  const { _username } = useApp();
  const { isConnected } = useAccount();
  const { connectWallet, disconnectWallet, formattedAddress, formattedBalance } = useWallet();
  const { disconnect } = useDisconnect();
  const navigate = useNavigate();
  const { 
    playerScore, 
    gamePlayers, 
    showHuntAgain, 
    onHuntAgain, 
    onLeaveGame 
  } = useGameStore();
  const { closeModal } = useWallet();

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (!isOpen) return null;

  return (
    <div className="mobile-left-sidebar fixed top-0 left-0 h-full w-72 bg-gray-900/95 backdrop-blur-lg border-r border-gray-700/50 shadow-xl transform transition-transform duration-300 ease-in-out z-50 lg:hidden translate-x-0">
      <div className="flex flex-col h-full">
        {/* Left Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
          <h2 className="text-lg font-bold text-white">Game Controls</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Left Sidebar Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          
          {/* Wallet Section */}
          <div className="sidebar-section space-y-2">
            <h3 className="text-sm font-medium text-gray-300">Wallet</h3>
            {!isConnected ? (
              <button 
                onClick={() => { connectWallet(); onClose(); }}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center gap-2"
              >
                <WalletIcon className="h-4 w-4" />
                Connect Wallet
              </button>
            ) : (
              <div className="space-y-2">
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <div className="text-sm text-gray-300">Connected</div>
                  <div className="text-xs text-gray-400">{formattedAddress}</div>
                  <div className="text-xs text-gray-400">{formattedBalance}</div>
                </div>
                <button 
                  onClick={() => { disconnectWallet(); closeModal(); onClose(); }}
                  className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>

          {/* Players Section (only on game page) */}
          {isGamePage && (
            <div className="sidebar-section space-y-2">
              <h3 className="text-sm font-medium text-gray-300">Players ({gamePlayers.length})</h3>
              <div className="h-32 overflow-y-auto bg-gray-800/50 rounded-lg p-2 space-y-1">
                {!gamePlayers || gamePlayers.length === 0 ? (
                  <div className="text-center py-4 text-gray-400">
                    <div className="text-xs">No players in game</div>
                  </div>
                ) : (
                  gamePlayers.map((player, index) => {
                    const username = player.username || `P${index + 1}`;
                    return (
                      <div key={`mobile-player-${username}-${index}`} className="flex items-center justify-between p-2 rounded bg-gray-700/50">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center font-bold text-white text-xs">
                            {username.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-xs text-white">
                            {username}
                          </span>
                        </div>
                        <div className="text-xs text-emerald-400">
                          {player.score || 0}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Game Actions Section (only on game page) */}
          {isGamePage && (
            <div className="sidebar-section space-y-2">
              <h3 className="text-sm font-medium text-gray-300">Game Actions</h3>
              {showHuntAgain && (
                <button 
                  onClick={() => { onHuntAgain(); onClose(); }}
                  className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  Hunt Again!
                </button>
              )}
              <button 
                onClick={() => { onLeaveGame(); onClose(); }}
                className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center gap-2"
              >
                Leave Game
              </button>
            </div>
          )}

          {/* Navigation Section */}
          <div className="sidebar-section space-y-2">
            <h3 className="text-sm font-medium text-gray-300">Navigation</h3>
            <button 
              onClick={handleBackToDashboard}
              className="w-full bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeftSidebar;
